/**
 * Complaint Intelligence Dashboard
 *
 * Surfaces the output of the complaint analysis layer (per-complaint +
 * batch). Users land here from the Complaints module and either:
 *   1. Run a batch analysis over a date window (default last 30 days), OR
 *   2. Reload an existing batch run from the sidebar.
 *
 * Three panels of intelligence are shown when a batch is loaded:
 *   - Spikes: dimensions whose volume jumped vs the trailing baseline
 *   - Themes: clusters of complaint narratives, ranked by severity x count
 *   - Summary: avg risk, mismatch rate, regulator/legal counts, top
 *     red-flag keywords, distribution by category and channel
 *
 * Plus a "Needs Review" sidebar showing recent high-risk complaints whose
 * per-complaint analyzer flagged them — driven off Phase 1, independent of
 * any specific batch run.
 *
 * Origin: Varo Bank meeting #2 — Karen + Dave, May 6 2026.
 */

import { useState, useMemo, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  ArrowPathIcon,
  PlayIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import toast from 'react-hot-toast';

import { complaintApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';

// ─── Types (mirror backend response shapes) ─────────────────────────────────

interface ThemeCluster {
  theme: string;
  count: number;
  severity: number;
  exampleComplaintIds: string[];
}

interface SpikeFinding {
  dimension: 'category' | 'channel' | 'regulatoryFlag' | 'theme';
  value: string;
  current: number;
  baseline: number;
  ratio: number;
  complaintIds: string[];
}

interface BatchSummary {
  avgRiskScore: number;
  needsReviewCount: number;
  classificationMismatchCount: number;
  classificationMismatchRate: number;
  regulatorEscalationCount: number;
  legalThreatCount: number;
  topRedFlagKeywords: Array<{ keyword: string; count: number }>;
  byCategory: Record<string, number>;
  byChannel: Record<string, number>;
  byRegulatoryFlag: Record<string, number>;
}

interface BatchAnalysisResult {
  id: string;
  startDate: string;
  endDate: string;
  filters: { status?: string; category?: string; priority?: string; channel?: string } | null;
  complaintCount: number;
  themes: ThemeCluster[];
  spikes: SpikeFinding[];
  summary: BatchSummary;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
}

interface NeedsReviewEntry {
  id: string;
  complaintId: string;
  combinedRiskScore: number;
  needsReviewReasons: string[];
  classificationMismatch: boolean;
  regulatoryFlags: string[];
  aiThemes: string[];
  analyzedAt: string;
  complaint: {
    id: string;
    referenceNumber: string;
    customerName: string;
    category: string;
    priority: string;
    status: string;
    subject: string;
    createdAt: string;
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function daysAgo(n: number): Date {
  const d = new Date(); d.setDate(d.getDate() - n); return d;
}
function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

// Color palette for charts
const PIE_COLORS = ['#0d9488', '#2563eb', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#ec4899', '#65a30d'];

const DIMENSION_LABEL: Record<SpikeFinding['dimension'], string> = {
  category: 'Category',
  channel: 'Channel',
  regulatoryFlag: 'Regulatory',
  theme: 'Theme',
};
const DIMENSION_BG: Record<SpikeFinding['dimension'], string> = {
  category: 'bg-blue-50 border-blue-200 text-blue-900',
  channel: 'bg-purple-50 border-purple-200 text-purple-900',
  regulatoryFlag: 'bg-red-50 border-red-200 text-red-900',
  theme: 'bg-amber-50 border-amber-200 text-amber-900',
};

function severityClass(score: number): string {
  if (score >= 80) return 'text-red-700 bg-red-50 border-red-200';
  if (score >= 60) return 'text-orange-700 bg-orange-50 border-orange-200';
  if (score >= 40) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-gray-700 bg-gray-50 border-gray-200';
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function ComplaintIntelligencePage() {
  const { teamId, isLoading: teamLoading } = useTeamId();
  const queryClient = useQueryClient();

  // Form state for the run config
  const [startDate, setStartDate] = useState(() => daysAgo(30).toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  // The currently-loaded batch (either freshly-run or reloaded from sidebar).
  const [activeBatch, setActiveBatch] = useState<BatchAnalysisResult | null>(null);

  // Recent batches sidebar
  const { data: batchListData } = useQuery({
    queryKey: ['complaint-batches', teamId],
    queryFn: () => complaintApi.listBatches(teamId!, 10).then(r => r.data),
    enabled: !!teamId,
  });

  // Needs-review list (lives independently of batch state)
  const { data: needsReviewData } = useQuery({
    queryKey: ['complaints-needs-review', teamId],
    queryFn: () => complaintApi.needsReview(teamId!, { limit: 25 }).then(r => r.data),
    enabled: !!teamId,
    refetchInterval: 30_000, // pick up new flagged complaints as they're analyzed
  });

  // Run-batch mutation
  const runBatchMutation = useMutation({
    mutationFn: (payload: {
      startDate: string;
      endDate: string;
      analyzeMissing?: boolean;
    }) => complaintApi.runBatchAnalysis(teamId!, payload).then(r => r.data),
    onSuccess: (data) => {
      setActiveBatch(data);
      queryClient.invalidateQueries({ queryKey: ['complaint-batches'] });
      toast.success(`Analyzed ${data.complaintCount} complaint${data.complaintCount === 1 ? '' : 's'}`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Batch analysis failed');
    },
  });

  // Reload-batch mutation
  const loadBatchMutation = useMutation({
    mutationFn: (batchId: string) => complaintApi.getBatch(teamId!, batchId).then(r => r.data),
    onSuccess: (data) => {
      setActiveBatch(data);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Could not load batch');
    },
  });

  // Exam-PDF export mutation — runs against the active batch.
  const exportPdfMutation = useMutation({
    mutationFn: () => complaintApi.getBatchPdf(teamId!, activeBatch!.id),
    onSuccess: (res) => {
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `clarid-complaint-analysis-${stamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'PDF export failed');
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error('Pick a start and end date');
      return;
    }
    runBatchMutation.mutate({
      startDate: new Date(startDate + 'T00:00:00').toISOString(),
      endDate: new Date(endDate + 'T23:59:59').toISOString(),
    });
  };

  const handleLast30 = () => {
    setStartDate(daysAgo(30).toISOString().slice(0, 10));
    setEndDate(new Date().toISOString().slice(0, 10));
  };
  const handleLast90 = () => {
    setStartDate(daysAgo(90).toISOString().slice(0, 10));
    setEndDate(new Date().toISOString().slice(0, 10));
  };

  // Convert summary distributions to chart-friendly arrays.
  const categoryChartData = useMemo(() => {
    if (!activeBatch) return [];
    return Object.entries(activeBatch.summary.byCategory)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [activeBatch]);

  const flagChartData = useMemo(() => {
    if (!activeBatch) return [];
    return Object.entries(activeBatch.summary.byRegulatoryFlag)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [activeBatch]);

  const themeBarData = useMemo(() => {
    if (!activeBatch) return [];
    return activeBatch.themes.slice(0, 10).map(t => ({
      theme: t.theme.length > 28 ? t.theme.slice(0, 25) + '…' : t.theme,
      count: t.count,
      severity: t.severity,
    }));
  }, [activeBatch]);

  if (teamLoading) {
    return <div className="animate-pulse h-64 bg-gray-200 rounded-lg" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <ArrowTrendingUpIcon className="h-7 w-7 text-teal-600" />
            Complaint Intelligence
          </h1>
          <p className="text-gray-600 mt-1">
            Pattern detection, classification validation, and risk scoring across your complaint volume.
          </p>
        </div>
        <Link
          to="/complaints"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-900 px-3 py-2 rounded-md hover:bg-teal-50 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to complaints
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* ─── Main column ─── */}
        <div className="lg:col-span-3 space-y-6">

          {/* Run-batch panel */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-navy-900 mb-1">Run a batch analysis</h2>
            <p className="text-sm text-gray-600 mb-4">
              Pick a date window. The analyzer reads pre-computed per-complaint analyses,
              clusters themes, detects spikes vs the prior window, and rolls up regulatory exposure.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Start</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">End</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={handleLast30}
                  className="text-xs text-teal-700 hover:text-teal-900 px-2 py-1 rounded border border-teal-200 hover:bg-teal-50">
                  Last 30
                </button>
                <button type="button" onClick={handleLast90}
                  className="text-xs text-teal-700 hover:text-teal-900 px-2 py-1 rounded border border-teal-200 hover:bg-teal-50">
                  Last 90
                </button>
              </div>
              <button type="submit" disabled={runBatchMutation.isPending}
                className="ml-auto inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-semibold text-sm">
                {runBatchMutation.isPending
                  ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Running…</>
                  : <><PlayIcon className="h-4 w-4" /> Run analysis</>
                }
              </button>
            </form>
          </section>

          {/* Empty state */}
          {!activeBatch && !runBatchMutation.isPending && (
            <section className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-12 text-center">
              <ArrowTrendingUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-700 font-semibold mb-1">No analysis loaded</p>
              <p className="text-sm text-gray-500">
                Run a new analysis above, or pick a recent run from the sidebar.
              </p>
            </section>
          )}

          {/* Active batch results */}
          {activeBatch && (
            <>
              {/* Header row with export */}
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  <span className="font-medium text-gray-700">{fmtDate(activeBatch.startDate)} → {fmtDate(activeBatch.endDate)}</span>
                  {' · '}
                  Run completed {activeBatch.completedAt ? new Date(activeBatch.completedAt).toLocaleString() : '—'}
                </div>
                <button
                  type="button"
                  onClick={() => exportPdfMutation.mutate()}
                  disabled={exportPdfMutation.isPending || activeBatch.status !== 'COMPLETED'}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 px-3 py-1.5 rounded-md"
                >
                  <DocumentArrowDownIcon className="h-4 w-4" />
                  {exportPdfMutation.isPending ? 'Generating…' : 'Export exam-ready PDF'}
                </button>
              </div>

              {/* Stat cards */}
              <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Complaints in window" value={activeBatch.complaintCount.toLocaleString()} />
                <Stat
                  label="Avg risk score"
                  value={String(activeBatch.summary.avgRiskScore)}
                  subtle={severityClass(activeBatch.summary.avgRiskScore)}
                />
                <Stat
                  label="Needs review"
                  value={activeBatch.summary.needsReviewCount.toLocaleString()}
                  subtle={activeBatch.summary.needsReviewCount > 0 ? 'text-amber-700 bg-amber-50 border-amber-200' : ''}
                />
                <Stat
                  label="Classification mismatch"
                  value={fmtPct(activeBatch.summary.classificationMismatchRate)}
                  subtle={activeBatch.summary.classificationMismatchRate > 0.15 ? 'text-red-700 bg-red-50 border-red-200' : ''}
                />
              </section>

              {/* Spikes */}
              <section className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-navy-900 flex items-center gap-2">
                    <ArrowTrendingUpIcon className="h-5 w-5 text-red-600" />
                    Spikes vs prior window
                  </h2>
                  <span className="text-xs text-gray-500">{activeBatch.spikes.length} finding(s)</span>
                </div>
                {activeBatch.spikes.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">
                    No dimensions exceeded the spike threshold (1.5× baseline, ≥3 absolute) for this window.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {activeBatch.spikes.slice(0, 12).map((s) => (
                      <div key={`${s.dimension}-${s.value}`}
                        className={`rounded-lg border p-3 ${DIMENSION_BG[s.dimension]}`}>
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="text-[10px] font-semibold uppercase tracking-wide opacity-75">
                            {DIMENSION_LABEL[s.dimension]}
                          </span>
                          <span className="text-xs font-bold tabular-nums">{s.ratio.toFixed(1)}× baseline</span>
                        </div>
                        <div className="text-sm font-semibold mb-1">{s.value}</div>
                        <div className="text-xs opacity-75">
                          {s.current} this window · {s.baseline} prior · {s.complaintIds.length} flagged
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Themes */}
              <section className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-navy-900">Top themes</h2>
                  <span className="text-xs text-gray-500">{activeBatch.themes.length} cluster(s)</span>
                </div>
                {activeBatch.themes.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">
                    No themes met the minimum cluster size (≥2 complaints sharing the tag).
                  </p>
                ) : (
                  <>
                    <div className="h-72 mb-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={themeBarData} layout="vertical" margin={{ top: 5, right: 30, left: 8, bottom: 5 }}>
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="theme" type="category" width={150} tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(value, _name, props) => [
                              `${value} (avg risk ${(props as any).payload?.severity ?? '?'})`,
                              'Complaints',
                            ]}
                          />
                          <Bar dataKey="count" fill="#0d9488" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                      {activeBatch.themes.slice(0, 12).map(t => (
                        <li key={t.theme} className="flex items-center justify-between border-b border-gray-100 py-1">
                          <span className="truncate" title={t.theme}>{t.theme}</span>
                          <span className="ml-2 flex items-center gap-2">
                            <span className="tabular-nums">{t.count}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${severityClass(t.severity)}`}>
                              {t.severity}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>

              {/* Distribution charts */}
              <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h3 className="text-sm font-semibold text-navy-900 mb-2">By complaint category</h3>
                  <div className="h-64">
                    {categoryChartData.length === 0 ? (
                      <p className="text-xs text-gray-500 italic">No data</p>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={categoryChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                            {categoryChartData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h3 className="text-sm font-semibold text-navy-900 mb-2">By regulatory flag</h3>
                  <div className="h-64">
                    {flagChartData.length === 0 ? (
                      <p className="text-xs text-gray-500 italic">No regulatory flags fired in this window.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={flagChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                            {flagChartData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </section>

              {/* Top red-flag keywords */}
              <section className="bg-white rounded-2xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-navy-900 mb-3">Top red-flag phrases</h2>
                {activeBatch.summary.topRedFlagKeywords.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">
                    No red-flag phrases matched in this window.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {activeBatch.summary.topRedFlagKeywords.map(k => (
                      <span key={k.keyword}
                        className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-900 text-xs px-2.5 py-1 rounded-full">
                        <span className="font-medium">{k.keyword}</span>
                        <span className="text-red-600 font-mono">×{k.count}</span>
                      </span>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        {/* ─── Sidebar ─── */}
        <div className="lg:col-span-1 space-y-4">
          {/* Needs review */}
          <section className="bg-white rounded-2xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-navy-900 flex items-center gap-1.5 mb-1">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
              Needs review
            </h3>
            <p className="text-[11px] text-gray-500 mb-3">
              Most recent complaints flagged by the analyzer. Refreshes every 30s.
            </p>
            {(needsReviewData?.flagged?.length ?? 0) === 0 ? (
              <p className="text-xs text-gray-500 italic">Nothing currently flagged.</p>
            ) : (
              <ul className="space-y-2">
                {(needsReviewData!.flagged as NeedsReviewEntry[]).slice(0, 8).map(entry => (
                  <li key={entry.id}>
                    <Link
                      to={`/complaints/${entry.complaintId}`}
                      className="block bg-amber-50/40 hover:bg-amber-50 border border-amber-200 rounded-lg p-2 transition-colors"
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-0.5">
                        <span className="text-xs font-mono text-gray-600 truncate">
                          {entry.complaint.referenceNumber}
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${severityClass(entry.combinedRiskScore)}`}>
                          {entry.combinedRiskScore}
                        </span>
                      </div>
                      <p className="text-xs text-gray-900 line-clamp-2 leading-tight">
                        {entry.complaint.subject}
                      </p>
                      {entry.needsReviewReasons[0] && (
                        <p className="text-[10px] text-amber-800 mt-1 line-clamp-1" title={entry.needsReviewReasons.join(' · ')}>
                          {entry.needsReviewReasons[0]}
                        </p>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Classification mismatches link */}
          <section className="bg-white rounded-2xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-navy-900 flex items-center gap-1.5 mb-1">
              <ScaleIcon className="h-4 w-4 text-blue-600" />
              Classification audit
            </h3>
            <p className="text-[11px] text-gray-500 mb-2">
              Complaints whose filed category disagrees with the analyzer's primary category.
            </p>
            {/* Placeholder: when Phase 4 lands we'll populate this with the real list. */}
            <p className="text-xs text-gray-400 italic">Surfaced inline on each complaint detail page.</p>
          </section>

          {/* Recent batches */}
          <section className="bg-white rounded-2xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-navy-900 mb-3">Recent runs</h3>
            {(batchListData?.batches?.length ?? 0) === 0 ? (
              <p className="text-xs text-gray-500 italic">No previous runs.</p>
            ) : (
              <ul className="space-y-2">
                {batchListData!.batches.slice(0, 8).map((b: any) => (
                  <li key={b.id}>
                    <button
                      onClick={() => loadBatchMutation.mutate(b.id)}
                      className={`w-full text-left rounded-lg border p-2 transition-colors ${
                        activeBatch?.id === b.id
                          ? 'bg-teal-50 border-teal-300'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="font-medium text-gray-900">
                          {fmtDate(b.startDate)} → {fmtDate(b.endDate)}
                        </span>
                        {b.status === 'COMPLETED' && <CheckCircleIcon className="h-3.5 w-3.5 text-green-600" />}
                        {b.status === 'FAILED' && <XCircleIcon className="h-3.5 w-3.5 text-red-600" />}
                        {b.status === 'RUNNING' && <ArrowPathIcon className="h-3.5 w-3.5 text-teal-600 animate-spin" />}
                      </div>
                      <p className="text-[11px] text-gray-500">
                        {b.complaintCount.toLocaleString()} complaint{b.complaintCount === 1 ? '' : 's'}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-component ──────────────────────────────────────────────────────────

function Stat({ label, value, subtle }: { label: string; value: string; subtle?: string }) {
  return (
    <div className={`rounded-2xl border p-4 ${subtle || 'border-gray-200 bg-white'}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-75 font-semibold mb-1">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
